import { useEffect, useState } from 'react';
import { ORB_TIP_FADE_MS, ORB_TIP_INTERVAL_MS, ORB_TIP_TYPING_MS } from './OrbBuddies.constants';

export type TipPhase = 'hidden' | 'typing' | 'shown';

export interface TipCycle {
  phase: TipPhase;
  text: string | null;
  /** Bumps each time a tip appears, so the buddies can greet it with a routine. */
  shownCount: number;
}

/**
 * Cycles through the tips: fade out, type for a moment, show the next one,
 * hold it. A new list starts over from its first tip.
 */
export const useTipCycle = (tips: readonly string[], instant: boolean): TipCycle => {
  const key = tips.join('\u0000');
  const [state, setState] = useState<{ index: number; phase: TipPhase; shownCount: number }>({
    index: 0,
    phase: 'hidden',
    shownCount: 0,
  });

  useEffect(() => {
    if (tips.length === 0) return undefined;
    const timers: number[] = [];
    const later = (ms: number, fn: () => void) => timers.push(window.setTimeout(fn, ms));
    const show = (index: number) => {
      setState((prev) => ({ index, phase: instant ? 'shown' : 'hidden', shownCount: prev.shownCount }));
      const reveal = () => {
        setState((prev) => ({ ...prev, phase: 'shown', shownCount: prev.shownCount + 1 }));
        later(ORB_TIP_INTERVAL_MS, () => show((index + 1) % tips.length));
      };
      if (instant) {
        reveal();
        return;
      }
      later(ORB_TIP_FADE_MS, () => {
        setState((prev) => ({ ...prev, phase: 'typing' }));
        later(ORB_TIP_TYPING_MS, reveal);
      });
    };
    show(0);
    return () => timers.forEach((id) => window.clearTimeout(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for the tips' content
  }, [key, instant]);

  if (tips.length === 0) return { phase: 'hidden', text: null, shownCount: state.shownCount };
  return { phase: state.phase, text: tips[state.index % tips.length] ?? null, shownCount: state.shownCount };
};
